import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Paper, Typography, Stack, Radio, RadioGroup, FormControlLabel, Box, Alert,
} from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { userApi } from '@/services/api';
import { selectUser, setUser } from '@/store/authSlice';

const LEVELS = ['clinic_only', 'scie_clinics', 'scie_clinics_ads'];

/**
 * Lets the client choose how their data may be used. Saves immediately on change
 * (independent of the profile form) and reflects the new value in the store.
 */
export default function PrivacySection() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [value, setValue] = useState(user?.dataConsent || 'clinic_only');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  const onChange = async (level) => {
    const prev = value;
    setValue(level);
    setSaving(true);
    setError(false);
    try {
      const updated = await userApi.setConsent(level);
      dispatch(setUser({ ...(user || {}), ...updated, dataConsent: level }));
    } catch (e) {
      setValue(prev);
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider', mt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <ShieldOutlinedIcon color="primary" />
        <Typography variant="subtitle1" fontWeight={800}>{t('privacy.title')}</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{t('privacy.help')}</Typography>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{t('privacy.error')}</Alert>}
      <RadioGroup value={value} onChange={(e) => onChange(e.target.value)}>
        {LEVELS.map((lv) => (
          <FormControlLabel key={lv} value={lv} disabled={saving}
            control={<Radio />}
            label={
              <Box sx={{ py: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>{t(`privacy.levels.${lv}.title`)}</Typography>
                <Typography variant="caption" color="text.secondary">{t(`privacy.levels.${lv}.desc`)}</Typography>
              </Box>
            } />
        ))}
      </RadioGroup>
    </Paper>
  );
}
